import { AxiosError } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';

import { api } from '@/api';
import { API_DOMAIN, catalogsMatcherEndpoint } from '@/constants';
import { apiUrl } from '@/env';
import { ICatalogsMatcherTaskCreate, ICatalogsMatcherTaskUpdate } from '@/interfaces/catalogs-matcher';
import { delay } from '@/utils';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';
import { State } from '../state';
import { readOneCatalogsMatcherTask } from './getters';
import {
  commitSetCatalogsMatcherTask, commitSetCatalogsMatcherTaskIsDownloading, commitSetCatalogsMatcherTasks,
  commitSetCatalogsMatcherTasksStat, commitSetCatalogsMatcherTaskStatus,
} from './mutations';
import { CatalogsMatcherTaskState } from './state';
import { authHeaders, downloadFile, withDefaultValue } from '../store-utils';

type MainContext = ActionContext<CatalogsMatcherTaskState, State>;

const MAX_FAILED_ATTEMPTS = 2 * 60; // 60 minutes
const ATTEMPTS_INTERVAL = 30 * 1000;  // 30 seconds
const API_URL = apiUrl + API_DOMAIN;
const DOWNLOAD_ONCE = true;

const utils = {
  counter: withDefaultValue({}),  // to control the number of failed attempts

  async downloadCatalogsMatcherTaskResult(context: MainContext, payload: { id: number, result: string }, once = false) {
    const url = `${API_URL}/${catalogsMatcherEndpoint}/${payload.id}/result`;
    const { token } = context.rootState.main;
    const { id, result } = payload;

    const updateStatuses = () => {
      commitSetCatalogsMatcherTaskIsDownloading(context, { id, isDownloading: false});
      commitSetCatalogsMatcherTaskStatus(context, { id, status: 'ENDED' });
    };

    if (once) {
      commitSetCatalogsMatcherTaskIsDownloading(context, { id, isDownloading: true });
      commitSetCatalogsMatcherTaskStatus(context, { id, status: 'DOWNLOAD' });
    } else {
      const updatedTask = readOneCatalogsMatcherTask(context)(id);
      if (updatedTask?.status !== 'EXPORT' && !updatedTask?.result?.endsWith('.zip')) {
        delete this.counter[id];
        return;
      }
    }

    // на случай, если захотим брать название файла отчта из store
    // const result = context.state.se_tasks.find((YtTask => YtTask.id === payload.id))?.result;
    // console.log('task.result --> ', result);

    try {
      const response = await fetch(url, authHeaders(token));
      if (response.status !== 200) throw new Error(`Response status: ${response.status}`);
      const bytes = await response.blob();
      delete this.counter[id];
      downloadFile(bytes, result);
      commitAddNotification(context, { content: 'Файл отчета успешно загружен', color: 'success' });
      updateStatuses();
    } catch (error) {
      if (once) {
        // console.log('Ошибка загрузки файла:', error);
        commitAddNotification(context, {
          content: 'Ошибка загрузки файла',
          color: 'error',
        });
        updateStatuses();
      } else if (this.counter[id] < MAX_FAILED_ATTEMPTS) {
          setTimeout(() => this.downloadCatalogsMatcherTaskResult(context, payload), ATTEMPTS_INTERVAL);
          this.counter[id]++;
        } else {
          delete this.counter[id];
          commitAddNotification(context, {
            content: 'Превышено максимальное количество попыток скачивания файла. Операция отменена',
            color: 'error' });
          updateStatuses();
        }
    }
  },
};


export const actions = {
  async actionGetCatalogsMatcherTasks(context: MainContext) {
    try {
      const { data } = await api.getCatalogsMatcherTasks(context.rootState.main.token);
      commitSetCatalogsMatcherTasks(context, data);
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionUpdateCatalogsMatcherTask(
    context: MainContext, payload: { id: number, task: ICatalogsMatcherTaskUpdate },
  ) {
    const { token } = context.rootState.main;
    const { id, task } = payload;
    try {
      const loadingNotification = { content: 'Сохранение', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const [{ data }] = await Promise.all([api.updateCatalogsMatcherTask(token, id, task), delay(500)]);
      commitSetCatalogsMatcherTask(context, data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно обновлена', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionCreateCatalogsMatcherTask(context: MainContext, payload: ICatalogsMatcherTaskCreate) {
    const { token } = context.rootState.main;
    try {
      const loadingNotification = { content: 'Создание', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const [{ data }] = await Promise.all([api.createCatalogsMatcherTask(token, payload), delay(500)]);
      commitSetCatalogsMatcherTask(context, data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно создана', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionDeleteCatalogsMatcherTask(context: MainContext, payload: { id: number }) {
    const { token } = context.rootState.main;
    try {
      const loadingNotification = { content: 'Удаление', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const [{ data }] = await Promise.all([api.deleteCatalogsMatcherTask(token, payload.id), delay(500)]);
      commitSetCatalogsMatcherTask(context, data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно удалена', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  // async actionRunCatalogsMatcherTask(context: MainContext, payload: { id: number }) {
  //   const { token } = context.rootState.main;
  //   const { id } = payload;
  //   try {
  //     const loadingNotification = { content: 'Запуск', showProgress: true };
  //     commitAddNotification(context, loadingNotification);
  //     await Promise.all([api.runCatalogsMatcherTask(token, id), delay(500)]);
  //     commitSetCatalogsMatcherTaskStatus(context, { id, status: 'ACTIVE' });
  //     commitRemoveNotification(context, loadingNotification);
  //     commitAddNotification(context, { content: 'Задача успешно запущена', color: 'success' });
  //   } catch (error) {
  //     await dispatchCheckApiError(context, error as AxiosError);
  //   }
  // },

  // async actionKillCatalogsMatcherTask(context: MainContext, payload: { id: number }) {
  //   const { token } = context.rootState.main;
  //   try {
  //     const loadingNotification = { content: 'Прерывание', showProgress: true };
  //     commitAddNotification(context, loadingNotification);
  //     const [{ data }] = await Promise.all([api.killCatalogsMatcherTask(token, payload.id), delay(500)]);
  //     commitSetCatalogsMatcherTask(context, data);
  //     commitRemoveNotification(context, loadingNotification);
  //     commitAddNotification(context, { content: 'Задача успешно прервана', color: 'success' });
  //   } catch (error) {
  //     await dispatchCheckApiError(context, error as AxiosError);
  //   }
  // },

  async actionStatCatalogsMatcherTasks(context: MainContext) {
    const { token } = context.rootState.main;
    try {
      const { data } = await api.statCatalogsMatcherTasks(token);
      commitSetCatalogsMatcherTasksStat(context, data);
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  // загружаем файл отчета
  async actionDownloadCatalogsMatcherTaskResult(context: MainContext, payload: { id: number, result: string }) {
    utils.downloadCatalogsMatcherTaskResult(context, payload, DOWNLOAD_ONCE);
  },

  async actionDownloadCatalogsMatcherTaskFile(context: MainContext, payload: { taskFile: string }) {
    const { taskFile } = payload;
    const url = `${API_URL}/${catalogsMatcherEndpoint}/task-file/${taskFile}`;
    const { token } = context.rootState.main;

    try {
      const response = await fetch(url, authHeaders(token));
      if (response.status !== 200) throw new Error(`Response status: ${response.status}`);
      const bytes = await response.blob();
      downloadFile(bytes, taskFile);
      commitAddNotification(context, { content: 'Файл отчета успешно загружен', color: 'success' });
    } catch (error) {
        commitAddNotification(context, {
          content: 'Ошибка загрузки файла',
          color: 'error',
        });
    }
  },

};

const { dispatch } = getStoreAccessors<CatalogsMatcherTaskState, State>('');

export const dispatchCreateCatalogsMatcherTask = dispatch(actions.actionCreateCatalogsMatcherTask);
export const dispatchGetCatalogsMatcherTasks = dispatch(actions.actionGetCatalogsMatcherTasks);
export const dispatchUpdateCatalogsMatcherTask = dispatch(actions.actionUpdateCatalogsMatcherTask);
export const dispatchDeleteCatalogsMatcherTask = dispatch(actions.actionDeleteCatalogsMatcherTask);
export const dispatchStatCatalogsMatcherTasks = dispatch(actions.actionStatCatalogsMatcherTasks);
export const dispatchDownloadCatalogsMatcherTaskResult = dispatch(actions.actionDownloadCatalogsMatcherTaskResult);
export const dispatchDownloadCatalogsMatcherTaskFile = dispatch(actions.actionDownloadCatalogsMatcherTaskFile);
